import { z } from "zod";

import { isPostalCodeValid } from "Forms/Utils/postalCodeUtils";
import {
  CountryCodes,
  GenderType,
  type Industry,
  LanguageCodes,
  type SaveActorRequest,
} from "Infrastructure/Api/Api";
import {
  getTranslation,
  Resources,
} from "Infrastructure/Translations/Resources";
import { dateToApiString } from "Shared/Utils/Format/date";
import {
  isCompanyNumberValid,
  isIBANValid,
  isPhoneValid,
  isSWIFTValid,
  isTaxNumberValid,
} from "Shared/Utils/validations";

export const actorFormSchema = z
  .object({
    firstName: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    lastName: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    email: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) })
      .check(
        z.email({
          error: () => getTranslation(Resources.Validation.InvalidEmail),
        }),
      ),
    phone: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) })
      .refine(isPhoneValid, {
        error: () => getTranslation(Resources.Validation.InvalidPhone),
      }),
    birthDate: z.date({
      error: () => getTranslation(Resources.Validation.Required),
    }),
    gender: z.enum(GenderType),
    language: z.enum(LanguageCodes),
    industries: z.array(z.string()).optional(),
    address: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    city: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    postalCode: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    country: z.enum(CountryCodes),
    iban: z.string().refine((iban) => isIBANValid(iban), {
      error: () => getTranslation(Resources.Validation.InvalidFormat),
    }),
    swift: z.string().refine((swift) => isSWIFTValid(swift), {
      error: () => getTranslation(Resources.Validation.InvalidFormat),
    }),
    hasCustomBillingDetails: z.boolean(),
    billingCompanyName: z.string().optional(),
    billingCompanyNumber: z
      .string()
      .optional()
      .refine((val) => !val || isCompanyNumberValid(val), {
        error: () => getTranslation(Resources.Validation.InvalidCompanyNumber),
      }),
    billingTaxNumber: z
      .string()
      .optional()
      .refine((val) => !val || isTaxNumberValid(val), {
        error: () => getTranslation(Resources.Validation.InvalidTaxNumber),
      }),
    billingAddress: z.string().optional(),
    billingCity: z.string().optional(),
    billingPostalCode: z.string().optional(),
    billingCountry: z
      .union([z.enum(CountryCodes), z.literal("")])
      .optional()
      .nullish(),
    billingIban: z
      .string()
      .optional()
      .refine((iban) => (iban ? isIBANValid(iban) : true), {
        error: () => getTranslation(Resources.Validation.InvalidFormat),
      }),
    billingSwift: z
      .string()
      .optional()
      .refine((swift) => (swift ? isSWIFTValid(swift) : true), {
        error: () => getTranslation(Resources.Validation.InvalidFormat),
      }),
  })
  .superRefine((data, ctx) => {
    if (data.postalCode && !isPostalCodeValid(data.postalCode, data.country)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: getTranslation(Resources.Validation.InvalidPostalCode),
        path: ["postalCode"],
      });
    }
    if (
      data.hasCustomBillingDetails &&
      data.billingPostalCode &&
      data.billingCountry &&
      !isPostalCodeValid(data.billingPostalCode, data.billingCountry)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: getTranslation(Resources.Validation.InvalidPostalCode),
        path: ["billingPostalCode"],
      });
    }
  });

export type ActorFormData = z.infer<typeof actorFormSchema>;

export function toSaveActorRequest(formData: ActorFormData): SaveActorRequest {
  return {
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    phone: formData.phone,
    birthDate: dateToApiString(formData.birthDate) ?? "",
    gender: formData.gender,
    language: formData.language,
    industries: (formData.industries ?? []) as Industry[],
    address: {
      street: formData.address,
      postalCode: formData.postalCode,
      municipality: formData.city,
      countryCode: formData.country,
    },
    iban: formData.iban,
    swift: formData.swift,
    hasCustomBillingDetails: formData.hasCustomBillingDetails,
    billingDetails: formData.hasCustomBillingDetails
      ? {
          companyName: formData.billingCompanyName ?? "",
          companyNumber: formData.billingCompanyNumber ?? "",
          taxNumber: formData.billingTaxNumber ?? "",
          iban: formData.billingIban ?? "",
          swift: formData.billingSwift ?? "",
          address: {
            street: formData.billingAddress ?? "",
            postalCode: formData.billingPostalCode ?? "",
            municipality: formData.billingCity ?? "",
            countryCode: (formData.billingCountry ?? "") as CountryCodes,
          },
        }
      : undefined,
  };
}
